import React, { useContext, useEffect, useRef, useState } from 'react'
import { useIntercom } from 'react-use-intercom'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { useHotkeys } from 'react-hotkeys-hook'
import { isMacOs, isMobileOnly } from 'react-device-detect'
import Modal from 'react-bootstrap/Modal'
import Spinner from 'react-bootstrap/Spinner'
import { useParams, useLocation } from 'react-router-dom'
import SidebarContext from 'Documents/publish/context/sidebarContext'
import Headers from 'Documents/publish/components/headers'
import Sections from 'Documents/publish/components/sections'
import Editor from 'Documents/publish/components/editor'
import Versions from 'Documents/publish/components/versions'
import SearchWrapper from 'Documents/publish/components/search/searchWrapper'
import { useTurboNavigate } from 'Shared/hooks/useTurboNavigate'
import { TabNav } from 'Shared/tabNav'
import TabBarTabItem from 'Documents/editor/tabBarTabItem'
import { StyledScroll } from 'Shared/styledScroll'

/**
 * @typedef {object} Config
 * @property {number} defaultVersionDefaultSectionPath
 * @property {string} defaultVersionPath
 * @property {string} headerColor
 * @property {string} headerTextColor
 * @property {string} slug
 * @property {string} slugPath
 * @property {string} theme
 * @property {string} themeColor
 * @property {string} title
 * @property {string} versionOptions
 * @property {string} versions
 * @property {string} workspaceId
 * @property {string} [logo]
 * @property {string} [intercomId]
 * @property {string} [headers]
 */

/**
 * @typedef {object} TopLevelSection
 * @property {number} id
 * @property {number} group_id
 * @property {string} item_type
 * @property {string} title
 * @property {string} color
 * @property {string} icon
 * @property {string} sections
 * @property {string} section_ids
 */

/**
 * @typedef {object} TabSection
 * @property {number} id
 * @property {string} title
 * @property {Section} firstSection
 * @property {string} color
 * @property {string} icon
 */

/**
 * @typedef {object} Section
 * @property {number} id
 * @property {number} group_id
 * @property {number} tab_id
 * @property {string} title
 * @property {string} color
 * @property {string} icon
 * @property {string} next_section
 * @property {string} previous_section
 * @property {string|null} slate_json
 * @property {string} last_modified
 */

/**
 * @typedef {object} Globals
 * @property {Config} [conf]
 * @property {string} environment
 * @property {Array<TopLevelSection>} sections
 * @property {Array<TabSection>} tabSections
 * @property {Section} section
 *
 * @typedef {Window & Globals} ExtendedWindow
 */

function usePrevious(value) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

const Breadcrumb = ({ path }) => (
  <>
    {path.map((title, index) => (
      <React.Fragment key={index}>
        <span className="me-2 text-nowrap text-truncate" style={{ opacity: '.75' }}>
          {title}
        </span>
        {index <= path.length && <i className="far fa-chevron-right me-2" style={{ fontSize: '11px', marginTop: '1.35px' }} />}
      </React.Fragment>
    ))}
  </>
)

Breadcrumb.propTypes = {
  path: PropTypes.arrayOf(PropTypes.string)
}

const Document = ({ config, sections, tabSections, section: currentSection, environment }) => {
  const portalRef = useRef()
  const { namespace, workspace, slug, version, section } = useParams()
  const { responsiveSidebarOpened, setResponsiveSidebarOpened } = useContext(SidebarContext)
  const [showSearchModal, setShowSearchModal] = useState(false)
  const turboNavigate = useTurboNavigate()
  const previousSection = usePrevious(section)
  const location = useLocation()
  const { hash } = location

  useEffect(() => {
    if ((!previousSection && section) || previousSection !== section) {
      turboNavigate(`/${namespace}/${workspace}/${slug}/${version}/${section}${hash}`, 'update_content', 'document_config')
    }
  }, [section, previousSection, turboNavigate, namespace, workspace, slug, version, hash])

  useHotkeys('mod+k', () => setShowSearchModal(true))

  const versions = config && config.versions ? JSON.parse(config.versions) : {}
  const versionId = versions[version] ? versions[version].id : null
  const { boot } = useIntercom()

  useEffect(() => {
    if (config && config.intercomId) {
      boot()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  let fileCdnPath = null
  let fileDownloadPath = null

  if (environment === 'stage') {
    fileCdnPath = 'https://stage.codegiant.io'
    fileDownloadPath = 'https://download.stage.codegiant.io'
  }

  if (environment === 'prod') {
    fileCdnPath = 'https://codegiant.io'
    fileDownloadPath = 'https://download.codegiant.io'
  }

  const navigateToHome = () => {
    turboNavigate(
      `/${namespace}/${workspace}/${slug}/${config.defaultVersionPath}/${config.defaultVersionDefaultSectionPath}`,
      'update_content',
      'document_config'
    )
  }

  const handleOnClick = () => {
    navigateToHome()
  }

  const findPathByGroupId = (/** number */ groupId, sections = [], tabSections = []) => {
    // Helper function to find section by id recursively
    const findSectionById = (id, sectionsArray) => {
      if (!sectionsArray) return null

      for (const section of sectionsArray) {
        if (section.id === id) return section
        if (section.sections) {
          let nestedSections
          try {
            nestedSections = JSON.parse(section.sections)
          } catch (error) {
            continue
          }
          const nestedSection = findSectionById(id, nestedSections)
          if (nestedSection) return nestedSection
        }
      }
      return null
    }

    // Helper function to find tabSection by id
    const findTabSectionById = id => {
      if (!tabSections) return null
      return tabSections.find(tabSection => tabSection.id === id)
    }

    // Recursive function to build the path
    const buildPath = groupId => {
      const section = findSectionById(groupId, sections)
      // If section is not found in sections, look in tabSections
      if (!section) {
        const tabSection = findTabSectionById(groupId)
        return tabSection ? [tabSection.title] : []
      }

      // Recursively find the path of the parent group
      const parentPath = buildPath(section.group_id)

      // Return the full path including the current section's title
      return [...parentPath, section.title]
    }

    // Start building the path from the given groupId
    return buildPath(groupId)
  }

  /**
   * Handle the KeyUp navigation for logo navigation
   * @param {React.KeyboardEvent<HTMLAnchorElement>} e
   * @returns {void}
   */
  const handleKeyUp = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      navigateToHome()
    }
  }

  /** @type Array<string> */
  const path = findPathByGroupId(currentSection.group_id, sections, tabSections)

  return (
    <DocumentMainWrapper>
      {/* Top Navigation Bar */}
      <TopBarContainer>
        <LogoTitleContainer>
          {config && config.logo ? (
            <a
              role="link"
              tabIndex="0"
              aria-label={`Return to ${config.defaultVersionDefaultSectionPath}`}
              title={`Return to ${config.defaultVersionDefaultSectionPath}`}
              onClick={handleOnClick}
              onKeyUp={handleKeyUp}
            >
              <Logo src={config.logo} onClick={handleOnClick} />
            </a>
          ) : (
            <a
              role="link"
              tabIndex="0"
              aria-label={`Return to ${config.defaultVersionDefaultSectionPath}`}
              title={`Return to ${config.defaultVersionDefaultSectionPath}`}
              onClick={handleOnClick}
              onKeyUp={handleKeyUp}
            >
              <Title>{config.title}</Title>
            </a>
          )}
        </LogoTitleContainer>
        <div className="d-flex align-items-center">
          <Headers
            headers={config && config.headers && config.headers.length > 0 ? JSON.parse(config.headers) : []}
            themeColor={config ? config.themeColor : null}
          />
          <SearchToggle
            onClick={() => {
              setShowSearchModal(true)
            }}
          >
            <SearchToggleLeft>
              <SearchToggleIcon>
                <i className="far fa-search" />
              </SearchToggleIcon>
              <SearchPlaceholder>Search...</SearchPlaceholder>
            </SearchToggleLeft>
            {!isMobileOnly && <SearchHotKey>{isMacOs ? <span>&#8984;K</span> : <span>Ctrl&nbsp;K</span>}</SearchHotKey>}
          </SearchToggle>
        </div>
      </TopBarContainer>
      <TabBarBreadCrumbWrapper>
        <ResponsiveSidebarToggle
          onClick={() => {
            setResponsiveSidebarOpened(!responsiveSidebarOpened)
          }}
        >
          <i className="fas fa-bars" />
        </ResponsiveSidebarToggle>
        <TabBarScroller>
          <div className="d-flex align-items-center ms-1" style={{ fontSize: '0.875rem' }}>
            <Breadcrumb path={path} />
            <span style={{ fontWeight: '500' }}>{currentSection.title}</span>
          </div>
        </TabBarScroller>
      </TabBarBreadCrumbWrapper>
      <TabBarWrapper>
        <Versions
          namespace={namespace}
          workspace={workspace}
          slug={slug}
          versionOptions={
            config && config.versionOptions && config.versionOptions.length > 0 ? JSON.parse(config.versionOptions) : []
          }
          versions={versions}
          version={version}
          themeColor={config ? config.themeColor : null}
        />
        <TabBarScroller>
          {tabSections.map(tab => (
            <TabBarTabItem
              key={tab.id}
              navRoute={`/${namespace}/${workspace}/${slug}/${version}/${tab.firstSection.id}`}
              style={{ padding: '0rem' }}
              themeColor={config.themeColor}
              rowData={tab}
              activeTabId={String(currentSection.tab_id)}
              nodes={[]}
              nestedIds={[]}
              portalRef={portalRef}
              readMode="PUBLISHED"
            />
          ))}
        </TabBarScroller>
      </TabBarWrapper>
      {/* Remaining Content */}
      <MainContentContainer>
        <SidebarContainer $responsiveSidebarOpened={responsiveSidebarOpened}>
          <VersionSectionsWrapper>
            <Sections
              topLevelSections={sections}
              themeColor={config ? config.themeColor : null}
              section={section}
              activeTabId={currentSection.tab_id}
              namespace={namespace}
              workspace={workspace}
              slug={slug}
              version={version}
            />
          </VersionSectionsWrapper>
        </SidebarContainer>
        <DocumentTopBarSectionEditorWrapper>
          <DocumentSidebarResponsiveLayover
            $responsiveSidebarOpened={responsiveSidebarOpened}
            onClick={() => {
              setResponsiveSidebarOpened(!responsiveSidebarOpened)
            }}
          />
          <DocumentEditorSectionEditorWrapper>
            {!currentSection || (section && currentSection && parseInt(currentSection.id) !== parseInt(section)) ? (
              <div className="d-flex align-items-center justify-content-center pt-5 pb-5 flex-grow-1">
                <Spinner animation="border" variant="primary" size="md" role="status">
                  <span className="sr-only">Loading...</span>
                </Spinner>
              </div>
            ) : (
              <Editor
                key={section}
                sectionData={currentSection}
                themeColor={config ? config.themeColor : null}
                namespace={namespace}
                workspace={workspace}
                slug={slug}
                version={version}
                currentSectionID={section}
                workspaceId={config ? config.workspaceId : null}
                fileCdnPath={fileCdnPath}
                fileDownloadPath={fileDownloadPath}
              />
            )}
          </DocumentEditorSectionEditorWrapper>
        </DocumentTopBarSectionEditorWrapper>
      </MainContentContainer>
      <SearchModal
        scrollable
        show={showSearchModal}
        onHide={() => {
          setShowSearchModal(false)
        }}
      >
        <SearchWrapper
          themeColor={config ? config.themeColor : null}
          versionId={versionId}
          setShowSearchModal={setShowSearchModal}
        />
      </SearchModal>
    </DocumentMainWrapper>
  )
}

export default Document

const DocumentMainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`

const TopBarContainer = styled.div`
  height: 65px;
  display: flex;
  min-height: 65px;
  padding: 0 15px;
  font-size: 0.9rem;
  font-weight: bold;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${props => props.theme.borderColor};
`

const TabBarWrapper = styled(TabNav)`
  flex-direction: row;
  @media (max-width: 992px) {
    display: none;
  }
`

const TabBarBreadCrumbWrapper = styled(TabNav)`
  flex-direction: row;
  display: none;
  color: ${props => props.theme.bsBodyColor};
  @media (max-width: 992px) {
    display: flex;
  }
`

const TabBarScroller = styled(StyledScroll)`
  display: flex;
  flex-grow: 1;
  flex-wrap: nowrap;
  overflow-x: auto;
  margin-bottom: -0.055rem;
`

const MainContentContainer = styled.section`
  display: flex;
  flex-direction: row;
  flex: 1;
  overflow: hidden;
`

const SidebarContainer = styled.div`
  display: flex;
  width: 280px;
  min-width: 280px;
  overflow: hidden;
  flex-direction: column;
  font-size: 0.9rem;
  background-color: ${props => props.theme.bgLightest};
  border-right: 1px solid ${props => props.theme.borderColor};
  @media (max-width: 992px) {
    display: ${props => (props.$responsiveSidebarOpened ? 'flex' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 102;
    border-right: 0px;
  }
`

const SearchModal = styled(Modal)`
  backdrop-filter: blur(4px);
`

const DocumentEditorSectionEditorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 100%;
  overflow: hidden;
`

const LogoTitleContainer = styled.div`
  text-decoration: none !important;
  display: flex;
  align-items: center;
  height: 65px;
  padding: 0 8px 0 0;
  justify-content: space-between;
  outline: none !important;
`

const Logo = styled.img`
  max-width: 100%;
  max-height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
`
const Title = styled.span`
  font-size: 18px;
  font-weight: 500;
  margin-right: 10px;
  color: ${props => props.theme.textGreyTintedDarker};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`

const VersionSectionsWrapper = styled.div`
  padding-top: 0.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const DocumentTopBarSectionEditorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  flex: 1;
`

const DocumentSidebarResponsiveLayover = styled.div`
  display: none;
  @media (max-width: 992px) {
    display: ${props => (props.$responsiveSidebarOpened ? 'block' : 'none')};
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    z-index: 99;
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
  }
`

const ResponsiveSidebarToggle = styled.div`
  cursor: pointer;
  display: none;
  font-size: 16px;
  color: ${props => props.theme.textGreyTintedDarker};
  @media (max-width: 992px) {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 8px;
  }
`

const SearchToggle = styled.div`
  display: flex;
  align-items: center;
  background: ${props => props.theme.bgLightestInverse};
  border-radius: 5px;
  min-width: 250px;
  padding: 6px 8px;
  font-weight: normal;
  font-size: 0.95rem;
  color: ${props => props.theme.textGreyTintedLighterInverse};
  justify-content: space-between;
  @media (max-width: 992px) {
    min-width: auto;
  }
`

const SearchToggleLeft = styled.div`
  display: flex;
`

const SearchPlaceholder = styled.span`
  @media (max-width: 992px) {
    display: none;
  }
`
const SearchToggleIcon = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  color: ${props => props.theme.textGreyTinted};
  margin-right: 10px;
  margin-top: 2px;
  font-size: 0.975rem;
  @media (max-width: 992px) {
    margin-right: 0;
  }
`
const SearchHotKey = styled.div`
  padding: 4px 5px;
  border: 1px solid ${props => props.theme.borderColor};
  border-radius: 4px;
  align-items: center;
  display: flex;
  justify-content: center;
  color: ${props => props.theme.textGreyTintedLighterInverse};
  font-size: 0.8rem;
  background: ${props => props.theme.bgSecondLightest};
  @media (max-width: 992px) {
    display: none;
  }
`
